import { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import * as authService from '../services/auth';
import { AUTH_EVENTS } from '../utils/constants';
import { onAuthEvent } from '../utils/authEvents';
import { unwrapDrf } from '../utils/helpers';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [isSigningUp, setIsSigningUp] = useState(false);

  // Initialize auth state from localStorage on mount
  useEffect(() => {
    const initializeAuth = () => {
      const currentUser = authService.getCurrentUser();
      const currentProfile = authService.getCurrentProfile();

      if (currentUser && currentProfile && authService.isAuthenticated()) {
        setUser(currentUser);
        setProfile(currentProfile);
      } else {
        // Clear invalid/expired auth data
        authService.clearAuthData();
      }

      setLoading(false);
    };

    initializeAuth();
  }, []);

  // Listen for auth events from API interceptor
  useEffect(() => {
    const cleanup = onAuthEvent(AUTH_EVENTS.UNAUTHORIZED, () => {
      // Auth error occurred (401, token refresh failed)
      setUser(null);
      setProfile(null);
      setError({ message: 'Your session has expired. Please log in again.' });

      // Note: We don't use navigate here to avoid import issues
      // Components will handle redirect based on isAuthenticated
    });

    return cleanup;
  }, []);

  /**
   * Login function - authenticates user and stores credentials
   */
  const login = useCallback(async (email, password) => {
    try {
      setError(null);
      setIsLoggingIn(true);

      const data = await authService.login(email, password);

      setUser(data.user);
      setProfile(data.profile);

      return data;
    } catch (err) {
      // Don't set error for pending accounts — Login.jsx redirects instead
      const approvalStatus = unwrapDrf(err?.data?.approval_status);
      if (approvalStatus !== 'pending') {
        setError(err);
      }
      throw err;
    } finally {
      setIsLoggingIn(false);
    }
  }, []);

  /**
   * Signup function - creates new customer account
   */
  const signup = useCallback(async (userData) => {
    try {
      setError(null);
      setIsSigningUp(true);

      // Signup returns a pending message — no auth state to set
      const data = await authService.signup(userData);
      return data;
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setIsSigningUp(false);
    }
  }, []);

  /**
   * Logout function - clears credentials and auth state
   */
  const logout = useCallback(async () => {
    try {
      await authService.logout();
    } catch (err) {
      // Log error but continue with logout
      console.error('Logout error:', err);
    } finally {
      setUser(null);
      setProfile(null);
      setError(null);
    }
  }, []);

  /**
   * Refresh profile data from server
   */
  const refreshProfile = useCallback(async () => {
    try {
      const updatedProfile = await authService.refreshUserProfile();
      setProfile(updatedProfile);
      if (updatedProfile.user) {
        setUser(updatedProfile.user);
      }
      return updatedProfile;
    } catch (err) {
      setError(err);
      throw err;
    }
  }, []);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Computed values for role checking
  const isAuthenticated = !!user && !!profile && authService.isAuthenticated();
  const isEmployee = profile?.role === 'employee';
  const isCustomer = profile?.role === 'customer';

  // Memoize context value to prevent unnecessary re-renders
  const value = useMemo(
    () => ({
      user,
      profile,
      loading,
      error,
      isLoggingIn,
      isSigningUp,
      isAuthenticated,
      isEmployee,
      isCustomer,
      login,
      signup,
      logout,
      refreshProfile,
      clearError,
    }),
    [
      user,
      profile,
      loading,
      error,
      isLoggingIn,
      isSigningUp,
      isAuthenticated,
      isEmployee,
      isCustomer,
      login,
      signup,
      logout,
      refreshProfile,
      clearError,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Custom hook to use the auth context
 * @returns {Object} Auth context value
 */
// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
